// hooks/useDeepSeekAPI.js
import { useState, useCallback } from 'react';
import axios from 'axios';

const useDeepSeekAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  //const [progress, setProgress] = useState(0);
  
  const apiKey = process.env.REACT_APP_DEEPSEEK_API_KEY;
  const apiUrl = process.env.REACT_APP_DEEPSEEK_API_URL;

  const analyzeProducts = useCallback(async (products, setProgress) => {
    if (!apiKey) {
      throw new Error('DeepSeek API Key não configurada');
    }

    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Divide em batches menores para melhor performance
      const BATCH_SIZE = 25; // Reduzido para evitar timeout
      const batches = [];
      
      for (let i = 0; i < products.length; i += BATCH_SIZE) {
        batches.push(products.slice(i, i + BATCH_SIZE));
      }

      const allResults = { produtos: [] };
      let processedBatches = 0;

      // CONTROLE DE CONCORRÊNCIA - máximo 3 batches simultâneos
      const MAX_CONCURRENT = 10;
      
      for (let i = 0; i < batches.length; i += MAX_CONCURRENT) {
        const currentBatches = batches.slice(i, i + MAX_CONCURRENT);
        
        // Processa batches em grupo (até MAX_CONCURRENT)
        const batchPromises = currentBatches.map((batch, index) => 
          processBatch(batch, i + index, setProgress)
        );

        const results = await Promise.all(batchPromises);
        
        results.forEach(result => {
          allResults.produtos.push(...result.produtos);
        });

        processedBatches += currentBatches.length;
        
        // Atualiza progresso
        const progress = Math.round((processedBatches / batches.length) * 100);
        setProgress(progress);
        
        console.log(`Processados ${processedBatches}/${batches.length} batches`);

        // Delay entre grupos de batches para evitar rate limit
        if (processedBatches < batches.length) {
          await new Promise(resolve => setTimeout(resolve, 5000)); // 2 segundos
        }
      }

      return allResults;

    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Erro na API';
      setError(errorMessage);
      setProgress(0);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [apiKey, apiUrl]);

  // Função para processar cada batch individualmente
  const processBatch = async (batchProducts, batchIndex, setProgress) => {
    const prompt = createOptimizedPrompt(batchProducts, batchIndex);

    try {
      const response = await axios.post(
        `${apiUrl}/chat/completions`,
        {
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: prompt }],
          response_format: { type: 'json_object' },
          max_tokens: 8000, // Reduzido para batches menores
          temperature: 0.1, // Mais baixo para consistência
        },
        {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
        }
      );

      return parseOptimizedResponse(response.data, batchProducts);
      
    } catch (error) {
      console.error(`Batch ${batchIndex} erro:`, error);
      return createOptimizedFallback(batchProducts);
    }
  };

  const createOptimizedPrompt = (products, batchIndex) => {
    return `PRODUTOS: ${JSON.stringify(products)}
RESPONDER COM JSON (Somente ncm e cst):
{"p":[["NCM","CST"]]}
Contexto: Produtos de Supermercado
IMPORTANTE: Classifique NCM PRIMEIRO
• NCM: 8 dígitos (EX: 2203.00.00)
• Use "NI" APENAS se realmente não conhecer, preencher como ["NI","  " ]
Regras CST(depois do NCM):
1. "04" para MONOFÁSICOS: cervejas, refrigerantes, bebidas, combustíveis, cigarros, energy drinks
2. "06" para ALÍQUOTA ZERO: produtos agrícolas in natura, alimentos básicos, leite, ovos, carnes, frutas, verduras
3. "  " para outros casos`;
  };

      const parseOptimizedResponse = (apiResponse, originalProducts) => {
  try {
    const content = apiResponse.choices[0].message.content;
    const data = JSON.parse(content);
    
    // NOVA ESTRUTURA: {"p": [["NCM", "CST"]]}
    if (data.p && Array.isArray(data.p)) {
      // Converte para estrutura interna
      return {
        produtos: data.p.map(([ncm, cst]) => ({
          ncm: ncm || "NI",
          cst: cst || "  "
        }))
      };
    }
      
      throw new Error('Estrutura inválida');
      
    } catch (error) {
      console.error('Parse error:', error);
      return createOptimizedFallback(originalProducts);
    }
  };

  const createOptimizedFallback = (products) => ({
  // Mantém a mesma estrutura de fallback
  produtos: products.map(() => ({
    ncm: "NI",
    cst: "  "
  }))
});

  return { analyzeProducts, loading, error };
};

export default useDeepSeekAPI;